import { FC, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Grid, Divider, Typography, useTheme } from '@mui/material'

import { useFetchAllViewQuery, useFetchObjectByCodeQuery } from '@redux/api'
import { isObjectValueType } from '@helpers'
import {
  ENTITY_TYPES_REQUEST,
  FIELD_VALUE_TYPE,
  OBJECT_FIELD_TYPE,
  SEARCH_FIELD_TYPE,
} from '@constants'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { GETDropDownFilter } from '@types'
import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'
import { FormInput } from '@microservices/wiskey-react-components'

import { SearchFilterForm } from './AddSimpleFilterDialog'
import { useValueInputs } from './hooks/useValueInputs'
import { useValueTypeOfSelectedValue } from './hooks/useValueTypeOfSelectedValue'

type SimpleFilterValueInputsProps = unknown

export const SimpleFilterValueInputs: FC<SimpleFilterValueInputsProps> = () => {
  const { t } = useTranslation()

  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext<SearchFilterForm>()

  const valueName = 'value'
  const valueNameInputName = 'value.name'
  const valuePathArrayName = 'value.valuePathArray'
  const valueTypeInputName = 'value.valueType'
  const asCheckboxInputName = 'value.asCheckbox'
  const asDurationInputName = 'value.asDuration'
  const fieldTypeInputName = 'value.fieldType'
  const formatDateInputName = 'value.formatDate'
  const timeIncrementInputName = 'value.timeIncrement'
  const objectValueInputName = 'value.objectValue'
  const objectValuePathArrayName = 'value.pathArray'
  const linkedObjectCodeInputName = 'value.linkedObjectCode'
  const defaultPlaceholderInputName = 'value.isDefaultPlaceholder'
  const placeholderValueInputName = 'value.placeholderValue'
  const placeholderStartInputName = 'value.placeholderStart'
  const placeholderEndInputName = 'value.placeholderEnd'
  const dropDownListCodeInputName = 'value.dropDownListCode'
  const dropDownWindowInputName = 'value.dropDownWindowCode'
  const dropDownListFiltersInputName = 'value.dropDownListFilters'
  const dropDownWindowFiltersInputName = 'value.dropDownWindowFilters'
  const valueMinName = 'value.min'
  const valueMaxName = 'value.max'
  const timeUnitName = 'value.timeUnit'

  const watchedValue = watch(valueName)
  const watchedValueName = watch(valueNameInputName)
  const watchedValuePathArray = watch(valuePathArrayName)
  const watchedValueType = watch(valueTypeInputName)
  const watchedAsCheckbox = watch(asCheckboxInputName)
  const watchedAsDuration = watch(asDurationInputName)
  const watchedFieldType = watch(fieldTypeInputName)
  const watchedTimeIncrement = watch(timeIncrementInputName)
  const watchedObjectValue = watch(objectValueInputName)
  const watchedDropDownListCode = watch(dropDownListCodeInputName)
  const watchedDropDownWindowCode = watch(dropDownWindowInputName)
  const watchedDefaultplaceholder = watch(defaultPlaceholderInputName)
  const watchedObject = watch('object')
  const watchedDropDownListFilters: GETDropDownFilter[] = watch(dropDownListFiltersInputName)
  const watchedDropDownWindowFilters: GETDropDownFilter[] = watch(dropDownWindowFiltersInputName)
  const watchedObjectValuePathArray = watch(objectValuePathArrayName)
  const watchedMin = watch(valueMinName)
  const watchedMax = watch(valueMaxName)
  const selectedObjectValue = watchedObjectValuePathArray?.[watchedObjectValuePathArray.length - 1]
  const selectedObjectValueType = selectedObjectValue?.valueType

  const { data: object } = useFetchObjectByCodeQuery(watchedObject?.id, {
    skip: !watchedObject?.id,
  })
  const objectFields = object?.fields

  const objectField = useMemo(() => {
    if (watchedValuePathArray && watchedValuePathArray.length > 0) {
      return { ...watchedValuePathArray[watchedValuePathArray.length - 1] }
    }
    if (watchedValueName?.label && objectFields) {
      const field = objectFields.find(f => f.name === watchedValueName.label)
      if (field) {
        return field
      }
    }
    return {}
  }, [watchedValuePathArray, watchedValueName, objectFields])

  const linkedObjectCode = objectField?.model ?? ''
  const isLinkedField = Boolean(objectField?.model)

  useValueTypeOfSelectedValue({
    objectField,
    valueTypeInputName,
  })

  const { data: dropdownEntities } = useFetchAllViewQuery(
    {
      objectCode: linkedObjectCode,
      viewType: ENTITY_TYPES_REQUEST.DROP_DOWN_ENTITY,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !linkedObjectCode,
    }
  )

  const { data: linkedObject } = useFetchObjectByCodeQuery(linkedObjectCode, {
    skip: !linkedObjectCode,
  })
  const linkedObjectFields = linkedObject?.fields
  const theme = useTheme()

  useEffect(() => {
    if (isLinkedField) {
      setValue(linkedObjectCodeInputName, linkedObjectCode)
    }
  }, [linkedObjectCode, isLinkedField, setValue, linkedObjectCodeInputName])

  const isValueEmbedded = isObjectValueType(watchedValueType)

  const {
    valueInput,
    valueTypeInput,
    asCheckboxInput,
    asDurationInput,
    formatDateInput,
    fieldTypeInput,
    defaultPlaceholderInput,
    placeholderValueInput,
    placeholderStartInput,
    placeholderEndInput,
    dropDownListInput,
    dropDownWindowInput,
    timeIncrementInput,
    minInput,
    maxInput,
    timeUnitInput,
  } = useValueInputs({
    objectValueInputName,
    valueNameInputName,
    asCheckboxInputName,
    asDurationInputName,
    fieldTypeInputName,
    formatDateInputName,
    timeIncrementInputName,
    watchedValueName,
    watchedValue: watchedValue || '',
    watchedDefaultplaceholder,
    linkedObjectFields,
    objectFields,
    watchedObject,
    defaultPlaceholderInputName,
    placeholderValueInputName,
    placeholderStartInputName,
    placeholderEndInputName,
    dropDownListCodeInputName,
    dropDownWindowInputName,
    dropdownEntities: dropdownEntities?.data || [],
    valueTypeInputName,
    valueType: watchedValueType,
    addedFiltersByObject: [],
    valueMinName,
    valueMaxName,
    watchedMin,
    watchedMax,
    timeUnitName,
  })

  const showFieldTypeInput = useMemo(
    () =>
      Boolean(watchedFieldType?.id) || watchedValueType === FIELD_VALUE_TYPE.DATETIME,
    [watchedFieldType, watchedValueType]
  )

  const isObjectValueTypeDateTime = useMemo(
    () => selectedObjectValueType === FIELD_VALUE_TYPE.DATETIME,
    [selectedObjectValueType]
  )

  const showFormatDateInput = useMemo(
    () =>
      (Boolean(watchedFieldType?.id) && watchedFieldType?.id !== SEARCH_FIELD_TYPE.DAILY_RANGE) ||
      isObjectValueTypeDateTime,
    [watchedFieldType, isObjectValueTypeDateTime]
  )

  const showTimeIncrementInput = useMemo(
    () => Boolean(watchedFieldType?.id) && watchedFieldType.id === SEARCH_FIELD_TYPE.RELATIVE_VALUE,
    [watchedFieldType]
  )

  const showMinMaxInputs = useMemo(
    () => Boolean(watchedFieldType?.id) && watchedFieldType.id === SEARCH_FIELD_TYPE.RELATIVE_VALUE,
    [watchedFieldType]
  )

  const shouldDisplayAsCheckboxField =
    (watchedValueType === FIELD_VALUE_TYPE.INTEGER ||
      watchedValueType === FIELD_VALUE_TYPE.BOOLEAN) &&
    objectField?.type !== OBJECT_FIELD_TYPE.ENUM

  return (
    <Grid container spacing={0} sx={{ mt: 0.5 }}>
      <Grid container item>
        <Divider sx={{ width: '100%', mb: 1 }} />
      </Grid>
      {/* Filter value */}
      <Grid container item>
        <Grid item flexGrow={1}>
          <ModalRefPathPicker
            hasClearInInput
            refPickAllowed
            embeddedObjectPickerControllerName={valuePathArrayName}
            isDisabled={!watchedObject?.id}
            isRequired
            label={t('searchFilterForm.value.label')}
            name={valueNameInputName}
            object={watchedObject}
            placeHolder={t('searchFilterForm.value.placeholder')}
            sxPickButton={{ m: 0, ml: 1 }}
          />
        </Grid>
        {watchedValueName && (
          <Grid container item flexGrow={1}>
            <Grid item flexGrow={1}>
              <FormInput {...valueTypeInput} disableClearable />
            </Grid>
            {shouldDisplayAsCheckboxField && (
              <Grid item>
                <FormInput {...asCheckboxInput} disableClearable disabled={watchedAsDuration} />
              </Grid>
            )}
            {watchedValueType === FIELD_VALUE_TYPE.INTEGER && (
              <Grid item>
                <FormInput {...asDurationInput} disableClearable disabled={watchedAsCheckbox} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>

      {/* General inputs */}
      {showFieldTypeInput && <FormInput {...fieldTypeInput} disableClearable />}
      {showTimeIncrementInput && (
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            <FormInput {...timeIncrementInput} disableClearable />
          </Grid>
          <Grid item xs={6}>
            <FormInput {...timeUnitInput} disableClearable />
          </Grid>
        </Grid>
      )}
      {showMinMaxInputs && (
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            <FormInput {...minInput} disableClearable />
          </Grid>
          <Grid item xs={6}>
            <FormInput {...maxInput} disableClearable />
          </Grid>
        </Grid>
      )}
      {isValueEmbedded && (
        <Grid container item>
          <Grid>
            <Typography
              sx={{ fontSize: '1rem', minWidth: 120, mt: 1, color: theme.palette.text.secondary }}
            >
              {t('searchFilterForm.objectValue.label')}
            </Typography>
          </Grid>
          <Grid item flexGrow={1}>
            <ModalRefPathPicker
              hasClearInInput
              isRequired
              embeddedObjectPickerControllerName={objectValuePathArrayName}
              name={objectValueInputName}
              object={objectField?.model}
              sxPickButton={{ m: 0, ml: 1 }}
            />
          </Grid>
        </Grid>
      )}
      {showFormatDateInput && <FormInput {...formatDateInput} disableClearable />}
      {/* Placeholder inputs */}
      <Grid container item alignItems={'center'} xs={12}>
        {watchedFieldType?.label === SEARCH_FIELD_TYPE.RANGE_OF_VALUES ? (
          <>
            <Grid item xs={5.5}>
              <FormInput {...placeholderStartInput} disableClearable />
            </Grid>
            <Grid item>
              <Divider sx={{ width: 10, mx: 0.5 }} />
            </Grid>
            <Grid item xs={4}>
              <FormInput {...placeholderEndInput} disableClearable label={undefined} />
            </Grid>
          </>
        ) : (
          <Grid item flexGrow={1}>
            <FormInput {...placeholderValueInput} disableClearable />
          </Grid>
        )}
        <Grid item>
          <FormInput {...defaultPlaceholderInput} disableClearable />
        </Grid>
      </Grid>
      <Grid container item>
        <Divider sx={{ width: '100%' }} />
      </Grid>
    </Grid>
  )
}
